import Disclosure from "./Disclosure";
import clsx from "clsx";
import {
  controlClass,
  describedBy,
  FieldError,
  HelpText,
  isRequiredNow,
  labelClass,
  RequiredMark,
  wrapperProps,
  type FieldProps,
} from "./shared";

/**
 * The port of fragments/forms/fields/select.php.
 *
 * The placeholder option carries an empty value and is disabled, so "Select
 * organisation type" can never be submitted as an answer: an untouched select
 * reads as empty and the required rules catch it.
 *
 * The PHP set is-placeholder only on the way out and nothing took it off again,
 * so a chosen answer stayed in the placeholder grey. Here data-placeholder
 * follows the value.
 */
export default function SelectField(props: FieldProps) {
  const {
    id,
    name,
    field,
    autocomplete,
    value,
    error,
    shown,
    onChange,
    onFocus,
  } = props;

  if (
    !id ||
    !name ||
    !field ||
    !field.options ||
    !Object.keys(field.options).length
  ) {
    return null;
  }

  const required = isRequiredNow(field, shown);
  const placeholder =
    field.placeholder ||
    `Select ${field.label.replace(/<[^>]*>/g, "").toLowerCase()}`;

  return (
    <div {...wrapperProps(props)}>
      <label className={labelClass} htmlFor={id}>
        {field.label} <RequiredMark field={field} />
      </label>

      <HelpText id={id} field={field} />

      <div className="relative">
        <select
          className={clsx(
            controlClass,
            "cursor-pointer appearance-none pr-7 data-[placeholder=true]:text-[#9ca3af] js-field-el",
          )}
          data-placeholder={value === ""}
          id={id}
          name={name}
          value={value}
          autoComplete={autocomplete || undefined}
          required={required}
          aria-required={required || undefined}
          aria-invalid={!!error}
          aria-describedby={describedBy(id, field)}
          data-validate="1"
          data-required={required ? "1" : undefined}
          onChange={(event) => onChange(name, event.target.value)}
          onFocus={() => onFocus(name)}
        >
          <option value="" disabled>
            {placeholder}
          </option>

          {Object.entries(field.options).map(([optionValue, optionLabel]) => (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          ))}
        </select>

        <svg
          className="pointer-events-none absolute top-1/2 right-1 -translate-y-1/2 text-brand-blue opacity-40"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <FieldError id={id} error={error} />

      <Disclosure id={id} disclosure={field.disclosure} />
    </div>
  );
}
